import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../CDN';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import MetricsCards from '../widgets/cards/MetricsCards';
import FAQWidget from '../widgets/interactive/FAQWidget';

type TeamsLandingPageStaticProps = {};

export const getStaticProps: GetStaticProps<TeamsLandingPageStaticProps> = async (context) => {
  const props: TeamsLandingPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const TeamsLandingPage: NextPageWithLayout<TeamsLandingPageStaticProps> = (props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.LandingPageTeams);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Microsoft Teams Integration"
        title="Generative AI helpdesk for instant employee support on MS Teams"
        description="Seamless Microsoft Teams integration for effortless AI-powered employee support! Our Generative AI chatbot automatically resolves 40% of issues through natural language. From password resets to time-off requests, our LLM-trained chatbot understands user intent and offers context-aware solutions, deflecting tickets and reducing manual work."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrowTop={true}>
        <MetricsCards
          records={[
            {
              metricValue: '40%',
              title: 'Issues automatically resolved',
            },
            {
              metricValue: '80%',
              title: 'Less manual work for your helpdesk teams',
            },
            {
              metricValue: '85%',
              title: 'Happier employees',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Gaspar AI & MS Teams Integration Key Features" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('landing-page-microsoft-teams-automated-resolution'),
              orientation: 'left',
              title: 'Automated resolution',
              description:
                'Gaspar AI’s Generative AI chatbot automatically and instantly resolves 40% of employee issues without human intervention, powered by LLMs and 3rd party plugins. Most common problems addressed include password resets, software provisioning, group and email management, and more!',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-microsoft-teams-automated-approvals'),
              orientation: 'right',
              title: 'Automated approvals',
              description:
                "Make it easy for everyone to ask for time-off or access the channels, folders and groups they need but not the ones they don't. The process happens on MS Teams for both the requester and the approver, and they can track the request’s status on the same platform.",
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-microsoft-teams-instant-accurate-human-like-answers'),
              orientation: 'left',
              title: 'Instant, accurate, human-like answers',
              description:
                'Your workforce can instantly find information - HR policies, IT troubleshooting and more just by asking our AI Virtual Agent. It searches articles, documents and data across knowledge bases and repositories, and can ask follow-up questions until employees get the exact answer.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-microsoft-teams-hassle-free-automated-ticketing'),
              orientation: 'right',
              title: 'Hassle-free, automated ticketing',
              description:
                "In case of auto-resolution, a ticket is automatically created on your ITSM platform. If the chatbot can't resolve the issue, it facilitates handoff to the right agent with easy ticket creation and smart routing. ",
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
TeamsLandingPage.displayName = 'TeamsLandingPage';
export default TeamsLandingPage;
